/* Onvor — Wishlist, UTM Attribution, Consent Management */

// ─── Consent ──────────────────────────────────────────────────────────────────

const CONSENT_KEY = 'onvor_consent_preferences';

function getConsent() {
  try {
    return JSON.parse(localStorage.getItem(CONSENT_KEY) || 'null');
  } catch {
    return null;
  }
}

function applyConsent(prefs) {
  if (typeof window.gtag === 'function') {
    window.gtag('consent', 'update', {
      analytics_storage: prefs.analytics ? 'granted' : 'denied',
      ad_storage: prefs.marketing ? 'granted' : 'denied',
      ad_user_data: prefs.marketing ? 'granted' : 'denied',
      ad_personalization: prefs.marketing ? 'granted' : 'denied',
    });
  }
  if (typeof window.fbq === 'function') {
    window.fbq('consent', prefs.marketing ? 'grant' : 'revoke');
  }
  if (typeof window.ttq !== 'undefined') {
    prefs.marketing ? window.ttq.grantConsent?.() : window.ttq.holdConsent?.();
  }
}

function syncConsentWithShopify(prefs) {
  if (typeof window.Shopify?.loadFeatures !== 'function') return;

  window.Shopify.loadFeatures(
    [{ name: 'consent-tracking-api', version: '0.1' }],
    (error) => {
      if (error || typeof window.Shopify?.customerPrivacy?.setTrackingConsent !== 'function') return;

      window.Shopify.customerPrivacy.setTrackingConsent(
        {
          analytics: Boolean(prefs.analytics),
          marketing: Boolean(prefs.marketing),
        },
        () => {}
      );
    }
  );
}

function openConsentPreferences() {
  const banner = document.getElementById('onvor-consent-banner');
  const initial = document.getElementById('onvor-consent-initial');
  const preferences = document.getElementById('onvor-consent-prefs');
  const analyticsToggle = document.getElementById('onvor-consent-analytics');
  const marketingToggle = document.getElementById('onvor-consent-marketing');
  const saved = getConsent();

  if (!banner || !initial || !preferences || !analyticsToggle || !marketingToggle) return;

  analyticsToggle.checked = Boolean(saved?.analytics);
  marketingToggle.checked = Boolean(saved?.marketing);
  initial.style.display = 'none';
  preferences.style.display = '';
  banner.style.display = '';
  banner.querySelector('input')?.focus();
}

window.OnvorConsent = {
  grant(analytics, marketing) {
    const previous = getConsent();
    const prefs = { essential: true, analytics, marketing, decided: true, timestamp: Date.now() };
    localStorage.setItem(CONSENT_KEY, JSON.stringify(prefs));
    document.cookie = `onvor_consent=1;max-age=${60 * 60 * 24 * 365};path=/;SameSite=Lax`;
    applyConsent(prefs);
    syncConsentWithShopify(prefs);
    if (marketing && !previous?.marketing && typeof window.fbq === 'function') {
      window.fbq('track', 'PageView');
    }
    const banner = document.getElementById('onvor-consent-banner');
    if (banner) banner.style.display = 'none';
  },
  revoke() {
    const prefs = { essential: true, analytics: false, marketing: false, decided: true, timestamp: Date.now() };
    localStorage.setItem(CONSENT_KEY, JSON.stringify(prefs));
    applyConsent(prefs);
    syncConsentWithShopify(prefs);
    const banner = document.getElementById('onvor-consent-banner');
    if (banner) banner.style.display = 'none';
  },
  openPreferences: openConsentPreferences,
};

document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;
  if (!event.target.closest('[data-onvor-consent-open]')) return;

  event.preventDefault();
  openConsentPreferences();
});

// Apply saved consent immediately on load
(function () {
  const saved = getConsent();
  if (saved?.decided) applyConsent(saved);
})();


// ─── UTM Attribution ──────────────────────────────────────────────────────────

const ATTRIBUTION_KEY = 'onvor_attribution';

function captureAttribution() {
  const params = new URLSearchParams(window.location.search);
  const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
  const clickIds = ['gclid', 'fbclid', 'ttclid'];
  const allKeys = [...utmKeys, ...clickIds];

  const hasAttribution = allKeys.some((k) => params.get(k));
  if (!hasAttribution) return;

  const data = {};
  allKeys.forEach((k) => { if (params.get(k)) data[k] = params.get(k); });
  data.landing_page = window.location.pathname + window.location.search;
  data.referrer = document.referrer || '';
  data.timestamp = Date.now();

  localStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(data));
  const encoded = encodeURIComponent(JSON.stringify(data));
  document.cookie = `${ATTRIBUTION_KEY}=${encoded};max-age=${60 * 60 * 24 * 30};path=/;SameSite=Lax`;
}

function syncAttributionToCart() {
  const raw = localStorage.getItem(ATTRIBUTION_KEY);
  if (!raw) return;

  let data;
  try { data = JSON.parse(raw); } catch { return; }

  const attributes = {};
  const map = {
    utm_source: '_utm_source', utm_medium: '_utm_medium', utm_campaign: '_utm_campaign',
    utm_term: '_utm_term', utm_content: '_utm_content',
    gclid: '_gclid', fbclid: '_fbclid', ttclid: '_ttclid',
    landing_page: '_landing_page', referrer: '_referrer',
  };
  Object.entries(map).forEach(([src, dest]) => {
    if (data[src]) attributes[dest] = data[src];
  });

  if (Object.keys(attributes).length === 0) return;

  fetch('/cart/update.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ attributes }),
  }).catch(() => {});
}

function handleCartLinesUpdate(event) {
  if (event.promise && typeof event.promise.then === 'function') {
    event.promise
      .then(({ detail } = {}) => {
        if (!detail?.didError) syncAttributionToCart();
      })
      .catch(() => {});
    return;
  }

  syncAttributionToCart();
}

captureAttribution();

// Ritual publishes this standard Shopify event after add, change, and remove
// requests complete. Syncing here ensures the attributes are present at checkout.
document.addEventListener('shopify:cart:lines-update', handleCartLinesUpdate);


// ─── Wishlist ─────────────────────────────────────────────────────────────────

const WISHLIST_KEY = 'onvor-wishlist';

function getItems() {
  try {
    const items = JSON.parse(localStorage.getItem(WISHLIST_KEY) || '[]');
    return Array.isArray(items)
      ? items.filter((item) => item && typeof item.handle === 'string' && item.handle.length > 0)
      : [];
  } catch {
    return [];
  }
}

function saveItems(items) {
  localStorage.setItem(WISHLIST_KEY, JSON.stringify(items));
}

function hasItem(handle) {
  return getItems().some((i) => i.handle === handle);
}

function toggleItem(item) {
  const items = getItems();
  const idx = items.findIndex((i) => i.handle === item.handle);
  if (idx >= 0) {
    items.splice(idx, 1);
  } else {
    items.push({ handle: item.handle, title: item.title, price: item.price, compareAt: item.compareAt || null, image: item.image || null });
  }
  saveItems(items);
  updateWishlistUI();
  renderWishlistDrawer();
  document.dispatchEvent(new CustomEvent('wishlist:updated', { detail: { items } }));
}

function updateWishlistUI() {
  const items = getItems();
  const count = items.length;
  const savedHandles = new Set(items.map((item) => item.handle));

  // Update count badges
  document.querySelectorAll('[data-wishlist-count]').forEach((el) => {
    el.textContent = count > 0 ? count : '';
    el.style.display = count > 0 ? '' : 'none';
  });

  // Update heart buttons
  document.querySelectorAll('.onvor-wishlist-btn[data-handle]').forEach((btn) => {
    const active = savedHandles.has(btn.dataset.handle);
    btn.classList.toggle('onvor-wishlist-btn--active', active);
    btn.setAttribute('aria-pressed', active ? 'true' : 'false');
  });
}

function getWishlistImageUrl(value) {
  if (!value) return null;

  try {
    const normalizedValue = value.startsWith('//') ? `https:${value}` : value;
    const imageUrl = new URL(normalizedValue, window.location.origin);
    if (imageUrl.protocol !== 'http:' && imageUrl.protocol !== 'https:') return null;
    imageUrl.searchParams.set('width', '120');
    return imageUrl.toString();
  } catch {
    return null;
  }
}

function renderWishlistDrawer() {
  const list = document.getElementById('onvor-wishlist-list');
  const empty = document.getElementById('onvor-wishlist-empty');
  if (!list) return;

  const items = getItems();
  list.innerHTML = '';

  if (items.length === 0) {
    if (empty) empty.style.display = '';
    return;
  }

  if (empty) empty.style.display = 'none';
  items.forEach((item) => {
    const li = document.createElement('li');
    li.className = 'onvor-wl-item';

    const imageUrl = getWishlistImageUrl(item.image);
    if (imageUrl) {
      const image = document.createElement('img');
      image.src = imageUrl;
      image.alt = String(item.title || '');
      image.className = 'onvor-wl-img';
      image.loading = 'lazy';
      image.width = 80;
      image.height = 100;
      li.appendChild(image);
    } else {
      const placeholder = document.createElement('div');
      placeholder.className = 'onvor-wl-img-placeholder';
      placeholder.setAttribute('aria-hidden', 'true');
      li.appendChild(placeholder);
    }

    const info = document.createElement('div');
    info.className = 'onvor-wl-info';

    const title = document.createElement('a');
    title.href = `/products/${encodeURIComponent(item.handle)}`;
    title.className = 'onvor-wl-title';
    title.textContent = String(item.title || '');
    info.appendChild(title);

    const price = document.createElement('span');
    price.className = 'onvor-wl-price';
    price.textContent = String(item.price || '');
    info.appendChild(price);
    li.appendChild(info);

    const remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'onvor-wl-remove';
    remove.dataset.handle = item.handle;
    remove.setAttribute('aria-label', `Remove ${item.title || 'item'} from wishlist`);
    remove.textContent = '×';
    li.appendChild(remove);

    list.appendChild(li);
  });

  list.querySelectorAll('.onvor-wl-remove').forEach((btn) => {
    btn.addEventListener('click', () => {
      const items = getItems().filter((i) => i.handle !== btn.dataset.handle);
      saveItems(items);
      updateWishlistUI();
      renderWishlistDrawer();
      document.dispatchEvent(new CustomEvent('wishlist:updated', { detail: { items } }));
    });
  });
}

function openWishlistDrawer() {
  const drawer = document.getElementById('onvor-wishlist-drawer');
  if (!drawer) return;
  renderWishlistDrawer();
  drawer.setAttribute('open', '');
  drawer.setAttribute('aria-hidden', 'false');
  document.body.style.overflow = 'hidden';
}

function closeWishlistDrawer() {
  const drawer = document.getElementById('onvor-wishlist-drawer');
  if (!drawer) return;
  drawer.removeAttribute('open');
  drawer.setAttribute('aria-hidden', 'true');
  document.body.style.overflow = '';
}

// Init wishlist on load
document.addEventListener('DOMContentLoaded', () => {
  updateWishlistUI();
  renderWishlistDrawer();

  // Toggle buttons on product cards / PDP
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('.onvor-wishlist-btn[data-handle]');
    if (!btn) return;
    e.preventDefault();
    toggleItem({
      handle: btn.dataset.handle,
      title: btn.dataset.title || '',
      price: btn.dataset.price || '',
      compareAt: btn.dataset.compareAt || null,
      image: btn.dataset.image || null,
    });
  });

  // Wishlist icon in header opens drawer
  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-wishlist-open]')) {
      e.preventDefault();
      openWishlistDrawer();
    }
    if (e.target.closest('[data-wishlist-close]')) closeWishlistDrawer();
    if (e.target.id === 'onvor-wishlist-overlay') closeWishlistDrawer();
  });

  // Escape key closes drawer
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeWishlistDrawer();
  });

  // Cross-tab sync
  window.addEventListener('storage', (e) => {
    if (e.key === WISHLIST_KEY) updateWishlistUI();
  });
});

window.OnvorWishlist = { getItems, hasItem, toggleItem, getCount: () => getItems().length };
